import type { ChargePointCluster } from '@/features/charge-points/types/charge-point.types';

export type ClusterStyle = 'square' | 'disc' | 'area' | 'halo' | 'outline';
export const CLUSTER_STYLES: ClusterStyle[] = [
  'square',
  'disc',
  'area',
  'halo',
  'outline',
];

export const CLUSTER_MIN_PX = 28;
export const CLUSTER_MAX_PX = 116;
/** Size contrast: diameter ∝ (count / biggest)^this. Lower = flatter */
export const CLUSTER_SIZE_EXPONENT = 0.35;

/** Charge points in a cluster (backend pin.number, else number of documents) */
export const clusterTotal = (c: ChargePointCluster) => c.pin?.number || c.count;

type Colors = NonNullable<ChargePointCluster['pin']['colors']>;

/** Per-status plug counts of two merged clusters */
function sumColors(a: ChargePointCluster, b: ChargePointCluster): Colors {
  const out: Colors = {};
  for (const c of [a.pin?.colors, b.pin?.colors]) {
    for (const [k, v] of Object.entries(c ?? {}) as [keyof Colors, number][])
      out[k] = (out[k] ?? 0) + (v ?? 0);
  }
  return out;
}

/** Share of plugs that are free right now (0..1), undefined without data */
export function clusterAvailableShare(c: ChargePointCluster) {
  const colors = c.pin?.colors;
  const total = Object.values(colors ?? {}).reduce((s, v) => s + (v ?? 0), 0);
  return total > 0 ? (colors?.green ?? 0) / total : undefined;
}

/**
 * Diameter on a power scale relative to the biggest cluster in view. Counts
 * span 2 to 18k+: sqrt (area ∝ count) squashes everything but the biggest
 * city to the minimum, a log scale makes 1.6k and 9.5k look the same.
 */
export function clusterDiameter(
  count: number,
  maxCount: number,
  maxPx: number = CLUSTER_MAX_PX,
  exponent: number = CLUSTER_SIZE_EXPONENT
): number {
  const max = Math.max(CLUSTER_MIN_PX, maxPx);
  if (maxCount <= 1 || count <= 1) return CLUSTER_MIN_PX;
  const ratio = Math.min(1, (count / maxCount) ** exponent);
  return Math.round(CLUSTER_MIN_PX + (max - CLUSTER_MIN_PX) * ratio);
}

/**
 * Largest bubble that fits the backend's grid on screen: the median distance
 * from each cluster to its nearest neighbour. Bigger bubbles than the grid
 * spacing pile up on top of each other in dense areas.
 */
export function gridFitMaxPx(points: { x: number; y: number }[]): number {
  if (points.length < 2) return CLUSTER_MAX_PX;
  const nearest = points
    .map((p, i) =>
      Math.min(
        ...points.map((q, j) =>
          i === j ? Infinity : Math.hypot(p.x - q.x, p.y - q.y)
        )
      )
    )
    .sort((a, b) => a - b);
  return nearest[Math.floor(nearest.length / 2)]!;
}

/**
 * The backend clusters on a geohash grid, so clusters sit in a regular
 * pattern and big ones overlap. Merge a cluster into a bigger one when its
 * centre falls inside the bigger bubble (biggest first), at the
 * count-weighted centroid. `toPx` projects to screen pixels.
 */
export function mergeTouchingClusters(
  clusters: ChargePointCluster[],
  toPx: (c: ChargePointCluster) => { x: number; y: number },
  maxPx: number = CLUSTER_MAX_PX,
  /** Also merge clusters closer than this (px), even if they don't overlap */
  mergePx = 0,
  exponent: number = CLUSTER_SIZE_EXPONENT
): ChargePointCluster[] {
  let list = [...clusters].sort((a, b) => clusterTotal(b) - clusterTotal(a));
  for (let merged = true; merged; ) {
    merged = false;
    const max = list.length ? clusterTotal(list[0]!) : 1;
    outer: for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]!;
        const b = list[j]!;
        const pa = toPx(a);
        const pb = toPx(b);
        // Merge when the bubbles overlap. With the log size scale a merged
        // cluster grows slowly, so this doesn't cascade into one giant
        const reach =
          (clusterDiameter(clusterTotal(a), max, maxPx, exponent) +
            clusterDiameter(clusterTotal(b), max, maxPx, exponent)) /
          2;
        if (
          Math.hypot(pa.x - pb.x, pa.y - pb.y) < Math.max(reach * 0.9, mergePx)
        ) {
          const na = clusterTotal(a);
          const nb = clusterTotal(b);
          const n = na + nb;
          list[i] = {
            ...a, // keep the bigger cluster's id so React keys stay stable
            count: a.count + b.count,
            pin: { ...a.pin, number: n, colors: sumColors(a, b) },
            coordinate: {
              lat: (a.coordinate.lat * na + b.coordinate.lat * nb) / n,
              lng: (a.coordinate.lng * na + b.coordinate.lng * nb) / n,
            },
          };
          list.splice(j, 1);
          list = list.sort((x, y) => clusterTotal(y) - clusterTotal(x));
          merged = true;
          break outer;
        }
      }
    }
  }
  return list;
}
